/**
 * Loads i18next translation resources from JSON files. Uses static imports so
 * the build works across all bundlers (webpack, rollup, vite).
 *
 * The import statements and the `modules` object between the "BEGIN GENERATED"
 * and "END GENERATED" markers are generated by `bazel run i18n`. Do not edit
 * them by hand — they are overwritten on the next run.
 */

import { ALL_LOCALES, type Locale } from "./locales";
import { ALL_NAMESPACES, type I18nNamespaceType } from "./namespaces";

type ModuleKey = `${Locale}/${I18nNamespaceType}`;
type TranslationRecord = Record<string, string>;

// --- BEGIN GENERATED IMPORTS ---
import deCustomerPortalShared from "./de/customer-portal-shared.json";
import deCustomerPortal from "./de/customer-portal.json";
import deI18n from "./de/i18n.json";
import deMerchantApp from "./de/merchant-app.json";
import deMerchantFulfillmentApp from "./de/merchant-fulfillment-app.json";
import deModel from "./de/model.json";
import deReturnApp from "./de/return-app.json";
import deWeb from "./de/web.json";
import enCustomerPortalShared from "./en/customer-portal-shared.json";
import enCustomerPortal from "./en/customer-portal.json";
import enI18n from "./en/i18n.json";
import enMerchantApp from "./en/merchant-app.json";
import enMerchantFulfillmentApp from "./en/merchant-fulfillment-app.json";
import enModel from "./en/model.json";
import enReturnApp from "./en/return-app.json";
import enWeb from "./en/web.json";
import esCustomerPortalShared from "./es/customer-portal-shared.json";
import esCustomerPortal from "./es/customer-portal.json";
import esI18n from "./es/i18n.json";
import esMerchantApp from "./es/merchant-app.json";
import esMerchantFulfillmentApp from "./es/merchant-fulfillment-app.json";
import esModel from "./es/model.json";
import esReturnApp from "./es/return-app.json";
import esWeb from "./es/web.json";
import frCustomerPortalShared from "./fr/customer-portal-shared.json";
import frCustomerPortal from "./fr/customer-portal.json";
import frI18n from "./fr/i18n.json";
import frMerchantApp from "./fr/merchant-app.json";
import frMerchantFulfillmentApp from "./fr/merchant-fulfillment-app.json";
import frModel from "./fr/model.json";
import frReturnApp from "./fr/return-app.json";
import frWeb from "./fr/web.json";
// --- END GENERATED IMPORTS ---

// --- BEGIN GENERATED MODULES ---
const modules: Record<ModuleKey, TranslationRecord> = {
  "de/customer-portal-shared": deCustomerPortalShared,
  "de/customer-portal": deCustomerPortal,
  "de/i18n": deI18n,
  "de/merchant-app": deMerchantApp,
  "de/merchant-fulfillment-app": deMerchantFulfillmentApp,
  "de/model": deModel,
  "de/return-app": deReturnApp,
  "de/web": deWeb,
  "en/customer-portal-shared": enCustomerPortalShared,
  "en/customer-portal": enCustomerPortal,
  "en/i18n": enI18n,
  "en/merchant-app": enMerchantApp,
  "en/merchant-fulfillment-app": enMerchantFulfillmentApp,
  "en/model": enModel,
  "en/return-app": enReturnApp,
  "en/web": enWeb,
  "es/customer-portal-shared": esCustomerPortalShared,
  "es/customer-portal": esCustomerPortal,
  "es/i18n": esI18n,
  "es/merchant-app": esMerchantApp,
  "es/merchant-fulfillment-app": esMerchantFulfillmentApp,
  "es/model": esModel,
  "es/return-app": esReturnApp,
  "es/web": esWeb,
  "fr/customer-portal-shared": frCustomerPortalShared,
  "fr/customer-portal": frCustomerPortal,
  "fr/i18n": frI18n,
  "fr/merchant-app": frMerchantApp,
  "fr/merchant-fulfillment-app": frMerchantFulfillmentApp,
  "fr/model": frModel,
  "fr/return-app": frReturnApp,
  "fr/web": frWeb,
};
// --- END GENERATED MODULES ---

export function getI18nResources(): Record<
  string,
  { translation: TranslationRecord }
> {
  const resources: Record<string, { translation: TranslationRecord }> = {};
  for (const locale of ALL_LOCALES) {
    resources[locale] = { translation: {} };
    for (const namespace of ALL_NAMESPACES) {
      const data = modules[`${locale}/${namespace}`];
      if (data) {
        Object.assign(resources[locale].translation, data);
      }
    }
  }
  return resources;
}
